(() => {
  'use strict';

  if (window.WeijmansMotion) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopPointer = window.matchMedia('(min-width: 950px) and (hover: hover) and (pointer: fine)');
  const lifecycle = new AbortController();
  const reveals = new Map();
  const parallax = new Map();
  const visibleParallax = new Set();
  const headers = new Set();
  const boundScrollWrappers = new WeakSet();
  let revealObserver;
  let parallaxObserver;
  let resizeObserver;
  let frame = 0;
  let needsMeasurement = false;
  let viewportHeight = window.innerHeight;
  let viewportTop = 0;
  let pageWrapper;
  let scrollContainer;
  let destroyed = false;

  const find = (root, selector) => {
    const nodes = [...root.querySelectorAll(selector)];
    if (root instanceof Element && root.matches(selector)) nodes.unshift(root);
    return nodes;
  };

  const restoreProperty = (element, property, previous) => {
    if (previous.value) element.style.setProperty(property, previous.value, previous.priority);
    else element.style.removeProperty(property);
  };

  const previousProperty = (element, property) => ({
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  });

  const settleReveal = (element, state) => {
    clearTimeout(state.timer);
    state.timer = 0;
    state.revealed = true;
    revealObserver?.unobserve(element);
    element.classList.remove('we-reveal-in');
    element.classList.add('we-revealed');
    restoreProperty(element, '--we-reveal-delay', state.previousDelay);
  };

  const reveal = (element, state) => {
    if (state.revealed) return;
    state.revealed = true;
    revealObserver?.unobserve(element);
    if (reducedMotion.matches || element.contains(document.activeElement)) {
      settleReveal(element, state);
      return;
    }
    element.style.setProperty('--we-reveal-delay', `${state.delay}ms`);
    element.classList.add('we-reveal-in');
    state.timer = window.setTimeout(() => settleReveal(element, state), 1200 + state.delay);
  };

  const createRevealObserver = () => {
    if (revealObserver || reducedMotion.matches || !('IntersectionObserver' in window)) return;
    try {
      revealObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const state = reveals.get(entry.target);
          if (state && entry.isIntersecting) reveal(entry.target, state);
        }
      }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' });
    } catch {
      revealObserver = undefined;
    }
  };

  const addReveal = (element, delay = 0) => {
    if (reveals.has(element) || element.matches('script, style, template, [data-we-no-reveal]')) return;
    const state = { delay, revealed: false, timer: 0, previousDelay: previousProperty(element, '--we-reveal-delay') };
    reveals.set(element, state);
    if (!revealObserver) {
      settleReveal(element, state);
      return;
    }
    try {
      revealObserver.observe(element);
    } catch {
      settleReveal(element, state);
    }
  };

  const canParallax = () => !reducedMotion.matches && desktopPointer.matches && 'IntersectionObserver' in window;

  const syncScrollContainer = () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper !== pageWrapper) {
      if (pageWrapper) resizeObserver?.unobserve(pageWrapper);
      pageWrapper = wrapper;
      if (pageWrapper) resizeObserver?.observe(pageWrapper);
    }
    if (pageWrapper && !boundScrollWrappers.has(pageWrapper)) {
      pageWrapper.addEventListener('scroll', onScroll, { signal: lifecycle.signal, passive: true });
      boundScrollWrappers.add(pageWrapper);
    }
    const overflowY = pageWrapper ? window.getComputedStyle(pageWrapper).overflowY : '';
    const scrollable = /^(auto|scroll|overlay)$/.test(overflowY);
    const temporarilyLocked = overflowY === 'hidden' && scrollContainer === pageWrapper;
    const hasViewport = pageWrapper && pageWrapper.clientHeight > 0;
    scrollContainer = hasViewport && (scrollable || temporarilyLocked) ? pageWrapper : undefined;
    viewportHeight = scrollContainer ? scrollContainer.clientHeight : window.innerHeight;
    viewportTop = scrollContainer ? scrollContainer.getBoundingClientRect().top + scrollContainer.clientTop : 0;
  };

  const renderFrame = () => {
    frame = 0;
    if (destroyed) return;
    if (needsMeasurement) syncScrollContainer();
    const scrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
    if (needsMeasurement) {
      needsMeasurement = false;
      for (const [element, state] of parallax) {
        if (!element.isConnected) continue;
        const rect = element.getBoundingClientRect();
        state.top = rect.top - viewportTop + scrollY - state.offset;
        state.height = rect.height;
      }
    }
    const scrolled = scrollY > 24;
    for (const header of headers) {
      if (header.classList.contains('is-scrolled') !== scrolled) header.classList.toggle('is-scrolled', scrolled);
    }
    if (!canParallax() || document.hidden) return;
    for (const element of visibleParallax) {
      const state = parallax.get(element);
      if (!state || !element.isConnected || !state.height) continue;
      const distance = scrollY + viewportHeight / 2 - state.top - state.height / 2;
      const offset = Math.max(-16, Math.min(16, distance / (viewportHeight + state.height) * 40));
      if (Math.abs(offset - state.offset) < 0.1) continue;
      state.offset = offset;
      element.style.setProperty('--we-parallax-y', `${offset.toFixed(2)}px`);
    }
  };

  const scheduleFrame = (measure = false) => {
    if (destroyed) return;
    needsMeasurement ||= measure;
    if (!frame) frame = window.requestAnimationFrame(renderFrame);
  };

  const stopParallax = () => {
    parallaxObserver?.disconnect();
    parallaxObserver = undefined;
    resizeObserver?.disconnect();
    resizeObserver = undefined;
    visibleParallax.clear();
    for (const [element, state] of parallax) {
      state.offset = 0;
      element.classList.remove('we-parallax-ready');
      restoreProperty(element, '--we-parallax-y', state.previousY);
    }
  };

  const startParallax = () => {
    if (!canParallax() || parallaxObserver || !parallax.size) return;
    try {
      parallaxObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) visibleParallax.add(entry.target);
          else visibleParallax.delete(entry.target);
        }
        scheduleFrame();
      }, { threshold: 0 });
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => scheduleFrame(true));
        resizeObserver.observe(document.documentElement);
        if (pageWrapper) resizeObserver.observe(pageWrapper);
      }
      for (const element of parallax.keys()) {
        parallaxObserver.observe(element);
        resizeObserver?.observe(element);
        element.classList.add('we-parallax-ready');
      }
      scheduleFrame(true);
    } catch {
      stopParallax();
    }
  };

  const initialize = (root = document) => {
    if (destroyed || !root?.querySelectorAll) return;
    createRevealObserver();
    const staggerChildren = new Map();
    for (const group of find(root, '[data-we-stagger]')) {
      [...group.children].forEach((element, index) => staggerChildren.set(element, Math.min(index * 55, 275)));
    }
    for (const [element, delay] of staggerChildren) addReveal(element, delay);
    for (const element of find(root, '[data-we-reveal]')) addReveal(element);
    for (const header of find(root, '[data-we-header]')) headers.add(header);
    for (const element of find(root, '[data-we-parallax]')) {
      if (parallax.has(element)) continue;
      parallax.set(element, { top: 0, height: 0, offset: 0, previousY: previousProperty(element, '--we-parallax-y') });
      if (parallaxObserver) {
        try {
          parallaxObserver.observe(element);
          resizeObserver?.observe(element);
          element.classList.add('we-parallax-ready');
        } catch {
          stopParallax();
        }
      }
    }
    startParallax();
    scheduleFrame(true);
  };

  const unload = root => {
    if (!(root instanceof Element)) return;
    for (const [element, state] of reveals) {
      if (element !== root && !root.contains(element)) continue;
      settleReveal(element, state);
      element.classList.remove('we-revealed');
      reveals.delete(element);
    }
    for (const [element, state] of parallax) {
      if (element !== root && !root.contains(element)) continue;
      parallaxObserver?.unobserve(element);
      resizeObserver?.unobserve(element);
      visibleParallax.delete(element);
      element.classList.remove('we-parallax-ready');
      restoreProperty(element, '--we-parallax-y', state.previousY);
      parallax.delete(element);
    }
    for (const header of headers) {
      if (header === root || root.contains(header)) {
        header.classList.remove('is-scrolled');
        headers.delete(header);
      }
    }
    if (!parallax.size) stopParallax();
    scheduleFrame(true);
  };

  const onPreferenceChange = () => {
    if (reducedMotion.matches) {
      for (const [element, state] of reveals) settleReveal(element, state);
      revealObserver?.disconnect();
      revealObserver = undefined;
    } else createRevealObserver();
    stopParallax();
    startParallax();
  };

  const onScroll = () => {
    if (headers.size || visibleParallax.size) scheduleFrame();
  };
  const options = { signal: lifecycle.signal };
  window.addEventListener('scroll', onScroll, { ...options, passive: true });
  window.addEventListener('resize', () => scheduleFrame(true), { ...options, passive: true });
  window.addEventListener('load', () => scheduleFrame(true), options);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduleFrame(true);
  }, options);
  document.addEventListener('shopify:section:load', event => initialize(event.target), options);
  document.addEventListener('shopify:section:unload', event => unload(event.target), options);
  document.addEventListener('focusin', event => {
    if (!(event.target instanceof Element)) return;
    for (const [element, state] of reveals) {
      if (element.contains(event.target)) settleReveal(element, state);
    }
  }, options);
  document.addEventListener('animationend', event => {
    if (event.animationName !== 'we-reveal') return;
    const state = reveals.get(event.target);
    if (state) settleReveal(event.target, state);
  }, options);
  reducedMotion.addEventListener('change', onPreferenceChange, options);
  desktopPointer.addEventListener('change', onPreferenceChange, options);

  window.WeijmansMotion = {
    refresh: initialize,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      lifecycle.abort();
      cancelAnimationFrame(frame);
      revealObserver?.disconnect();
      stopParallax();
      for (const [element, state] of reveals) {
        settleReveal(element, state);
        element.classList.remove('we-revealed');
      }
      for (const header of headers) header.classList.remove('is-scrolled');
      reveals.clear();
      parallax.clear();
      headers.clear();
      delete window.WeijmansMotion;
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { ...options, once: true });
  else initialize();
  document.fonts?.ready.then(() => scheduleFrame(true)).catch(() => {});
})();
